import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { INTERNAL_API_KEY_USAGE_PAGE_SIZE } from "../constants/internalApiKeys.constants";
import { InternalApiKeysService } from "../services/internalApiKeys.service";
import type { InternalApiKeyUsageEntry } from "../types/internalApiKeys.types";

export interface UseInternalApiKeyUsageResult {
  entries: InternalApiKeyUsageEntry[];
  total: number;
  page: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

interface UsageRead {
  key: string;
  entries: InternalApiKeyUsageEntry[];
  total: number;
  error: string | null;
}

/** One key's usage log, newest first. `keyId` null means the drawer is closed and nothing is read. */
export function useInternalApiKeyUsage(keyId: string | null): UseInternalApiKeyUsageResult {
  const [paging, setPaging] = useState<{ keyId: string | null; page: number }>({ keyId, page: 1 });
  const [nonce, setNonce] = useState(0);
  const [read, setRead] = useState<UsageRead | null>(null);

  const page = paging.keyId === keyId ? paging.page : 1;
  const key = `${keyId ?? "none"}:${page}:${nonce}`;

  useEffect(() => {
    if (!keyId) return;
    let cancelled = false;
    InternalApiKeysService.usage(keyId, {
      skip: (page - 1) * INTERNAL_API_KEY_USAGE_PAGE_SIZE,
      limit: INTERNAL_API_KEY_USAGE_PAGE_SIZE,
    })
      .then((result) => {
        if (!cancelled) setRead({ key, entries: result.data, total: result.total, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) setRead({ key, entries: [], total: 0, error: HttpApiService.getErrorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [key, keyId, page]);

  const setPage = useCallback((next: number) => setPaging({ keyId, page: next }), [keyId]);
  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const current = read?.key === key ? read : null;

  return {
    entries: current?.entries ?? [],
    total: current?.total ?? 0,
    page,
    setPage,
    isLoading: keyId !== null && current === null,
    error: current?.error ?? null,
    reload,
  };
}
